/**
 * PursuitFeedbackPanel — small floating feedback note, opened from the
 * "Ask this Pursuit" launcher's expand menu. Deliberately lightweight
 * (not a Sheet, not tied to the Support Tickets system) — just a quick
 * note the user can leave about the pursuit or the page they're on.
 */
import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { useAuth } from '../../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PursuitFeedbackPanel({ open, onOpenChange, pursuitId }) {
  const location = useLocation();
  const { getAuthHeaders } = useAuth();
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  if (!open) return null;

  const handleClose = () => {
    if (sending) return;
    onOpenChange(false);
  };

  const handleSend = async () => {
    const trimmed = message.trim();
    if (!trimmed || sending) return;
    setSending(true);
    try {
      const res = await fetch(`${API_URL}/api/pursuits/${pursuitId}/feedback`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ message: trimmed, page_path: location.pathname }),
      });
      if (!res.ok) throw new Error('Request failed');
      toast.success('Thanks — feedback sent');
      setMessage('');
      onOpenChange(false);
    } catch (e) {
      toast.error('Could not send feedback. Try again.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      data-testid="pursuit-feedback-panel"
      className="fixed bottom-24 right-6 z-50 w-80 bg-white rounded-lg shadow-2xl border border-slate-200 flex flex-col"
    >
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
        <span className="font-semibold text-sm text-slate-900">Feedback</span>
        <button
          type="button"
          onClick={handleClose}
          aria-label="Close feedback"
          className="text-slate-400 hover:text-slate-700"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
      <div className="p-3 space-y-2">
        <p className="text-xs text-slate-500">
          Something off, or an idea? Let us know.
        </p>
        <Textarea
          rows={4}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="What's on your mind?"
          autoFocus
          data-testid="pursuit-feedback-textarea"
        />
        <div className="flex justify-end">
          <Button
            size="sm"
            onClick={handleSend}
            disabled={sending || !message.trim()}
            data-testid="pursuit-feedback-send-btn"
          >
            {sending ? 'Sending...' : 'Send'}
          </Button>
        </div>
      </div>
    </div>
  );
}
